import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StickyMobileCta from '../components/StickyMobileCta';
import { Phone, MessageCircle, Mail, MapPin, AlertTriangle, Stethoscope } from 'lucide-react';
import { Button } from '../components/ui/button';
import { CONTACT } from '../data/mock';
import { trackCallClick, trackWhatsAppClick } from '../lib/analytics';

const SITE = 'https://ichiandoripetcare.com';
const SLUG = 'home-veterinary-visit-noida';
const TITLE = 'Home Veterinary Visit in Noida | Contact Us | Ichi & Ori';
const DESCRIPTION = 'We currently do not offer home veterinary visits in Noida. For emergencies, contact Ichi and Ori Pet Care — we are happy to help with online consultations and guidance.';

const setMeta = (sel, attr, value) => {
  const el = document.head.querySelector(sel);
  if (el) el.setAttribute(attr, value);
};

const HomeVetNotice = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const canonical = `${SITE}/${SLUG}`;
    const original = {
      desc: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      can: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
      ogt: document.head.querySelector('meta[property="og:title"]')?.getAttribute('content'),
      ogd: document.head.querySelector('meta[property="og:description"]')?.getAttribute('content'),
      ogu: document.head.querySelector('meta[property="og:url"]')?.getAttribute('content'),
    };
    setMeta('meta[name="description"]', 'content', DESCRIPTION);
    setMeta('link[rel="canonical"]', 'href', canonical);
    setMeta('meta[property="og:title"]', 'content', TITLE);
    setMeta('meta[property="og:description"]', 'content', DESCRIPTION);
    setMeta('meta[property="og:url"]', 'content', canonical);
    return () => {
      if (original.desc != null) setMeta('meta[name="description"]', 'content', original.desc);
      if (original.can != null)  setMeta('link[rel="canonical"]', 'href', original.can);
      if (original.ogt != null)  setMeta('meta[property="og:title"]', 'content', original.ogt);
      if (original.ogd != null)  setMeta('meta[property="og:description"]', 'content', original.ogd);
      if (original.ogu != null)  setMeta('meta[property="og:url"]', 'content', original.ogu);
    };
  }, []);

  const whatsappLink = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent('Hi, I had a question about home veterinary care for my pet in Noida.')}`;

  return (
    <div data-testid="home-vet-notice" className="bg-[#fff8fb] min-h-screen">
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>

      <section className="max-w-[760px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-24">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#e870b6]/15 border border-[#e870b6]/30 text-[#5b3eb5] text-xs font-bold tracking-[0.18em] mb-6">
          <Stethoscope className="w-3.5 h-3.5" /> HOME VETERINARY VISITS — NOIDA
        </span>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] text-[#1a0b3d] mb-5">
          Home vet visits aren&rsquo;t something we currently offer
        </h1>

        <p className="text-base sm:text-lg text-[#3b1d76] leading-relaxed mb-4">
          We&rsquo;re focused right now on giving every dog and cat our full attention at our clinic in Sector 90, Noida, so we&rsquo;ve held off on launching a home-visit service until we can do it as well as we&rsquo;d want for our own pets.
        </p>

        <div className="rounded-2xl border-2 border-[#e870b6]/30 bg-white p-6 sm:p-7 my-8 shadow-sm" data-testid="emergency-notice">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-6 h-6 text-[#e870b6] flex-shrink-0 mt-0.5" />
            <h2 className="text-xl sm:text-2xl font-bold text-[#1a0b3d]">In an emergency? Please reach out anyway.</h2>
          </div>
          <p className="text-[#3b1d76] leading-relaxed mb-4">
            If your pet is unwell and you can&rsquo;t bring them to the clinic, contact us. We&rsquo;re always happy to help with an <strong>online consultation</strong> over WhatsApp video, suggest immediate first-aid steps, or point you to the right 24-hour hospital in Delhi-NCR for the situation. It&rsquo;s never a bother.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('home_vet_notice')} data-testid="notice-cta-call">
            <Button className="w-full bg-[#5b3eb5] hover:bg-[#4a2f9a] text-white rounded-full py-6 text-base font-semibold shadow-lg">
              <Phone className="w-5 h-5 mr-2" /> Call {CONTACT.phone}
            </Button>
          </a>
          <a href={whatsappLink} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('home_vet_notice')} data-testid="notice-cta-whatsapp">
            <Button className="w-full bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full py-6 text-base font-semibold shadow-lg">
              <MessageCircle className="w-5 h-5 mr-2" /> Chat on WhatsApp
            </Button>
          </a>
        </div>

        <div className="rounded-2xl bg-white border border-[#f5d3e6] p-6 sm:p-7 shadow-sm space-y-4" data-testid="notice-contact-block">
          <h2 className="text-lg sm:text-xl font-bold text-[#1a0b3d]">Contact Ichi &amp; Ori Pet Care</h2>
          <div className="flex items-start gap-3 text-[#3b1d76]">
            <Phone className="w-5 h-5 text-[#e870b6] flex-shrink-0 mt-0.5" />
            <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('home_vet_notice_info')} className="hover:text-[#e870b6]">{CONTACT.phone}</a>
          </div>
          <div className="flex items-start gap-3 text-[#3b1d76]">
            <Mail className="w-5 h-5 text-[#e870b6] flex-shrink-0 mt-0.5" />
            <a href={`mailto:${CONTACT.email}`} className="hover:text-[#e870b6] break-all">{CONTACT.email}</a>
          </div>
          <div className="flex items-start gap-3 text-[#3b1d76]">
            <MapPin className="w-5 h-5 text-[#e870b6] flex-shrink-0 mt-0.5" />
            <span>{CONTACT.address}</span>
          </div>
          {CONTACT.hours && (
            <p className="text-sm text-[#3b1d76]/80 pt-1">Hours: {CONTACT.hours}</p>
          )}
        </div>

        <p className="text-sm text-[#3b1d76]/80 mt-8">
          Looking for something else? Explore our <Link to="/veterinary" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]">veterinary services</Link>, <Link to="/grooming" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]">grooming</Link>, <Link to="/boarding" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]">day boarding</Link>, or head back to the <Link to="/" className="text-[#5b3eb5] font-semibold underline hover:text-[#e870b6]">homepage</Link>.
        </p>
      </section>
      <StickyMobileCta source="homevetnotice" />
    </div>
  );
};

export default HomeVetNotice;
